<template>
    <div class="device-status-container">
        <div class="status-grid">
            <div
                v-for="(status, index) in deviceStatusList"
                :key="index"
                class="status-item"
                :class="`status-${status.type}`"
            >
                <div class="status-icon">
                    <img :src="status.icon" :alt="status.name" class="icon-img">
                </div>
                <div class="status-info">
                    <div class="status-name" :style="statusNameStyle">{{ status.name }}</div>
                    <div class="status-count" :style="statusCountStyle">{{ status.count }}</div>
                </div>
            </div>
        </div>
        <DashboardLoading
            :visible="loading"
            text="加载设备运行状态数据"
            size="normal"
        />
    </div>
</template>

<script>
import DashboardLoading from './DashboardLoading.vue'

export default {
  name: 'DeviceStatus',
  components: {
    DashboardLoading
  },
  props: {
    fontSizes: {
      type: Object,
      default: () => ({
        titleFontSize: 16,
        legendFontSize: 12,
        axisLabelFontSize: 10,
        axisNameFontSize: 12,
        tooltipFontSize: 14,
        dataLabelFontSize: 11,
        smallFontSize: 8,
        largeFontSize: 18
      })
    },
    machineReportData: {
      type: Object,
      default: () => ({
        normal: 0,
        offLine: 0,
        error: 0,
        expire: 0
      })
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      deviceStatusList: [
        {
          name: '运行中',
          type: 'running',
          count: 1996,
          icon: require('@/assets/images/dashboard/runing.png')
        },
        {
          name: '离线',
          type: 'offline',
          count: 42,
          icon: require('@/assets/images/dashboard/offline.png')
        },
        {
          name: '故障',
          type: 'error',
          count: 73,
          icon: require('@/assets/images/dashboard/error.png')
        },
        {
          name: '过期',
          type: 'expire',
          count: 157,
          icon: require('@/assets/images/dashboard/expire.png')
        }
      ]
    }
  },
  computed: {
    // 状态名称样式
    statusNameStyle () {
      return {
        fontSize: `${this.fontSizes.legendFontSize}px`
      }
    },
    // 状态数量样式
    statusCountStyle () {
      return {
        fontSize: `${this.fontSizes.largeFontSize}px`
      }
    }
  },
  watch: {
    // 监听props中的machineReportData变化
    machineReportData: {
      handler (newData) {
        if (newData) {
          this.updateDeviceStatusList()
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted () {
    // 初始化时更新设备状态列表
    this.updateDeviceStatusList()
  },
  methods: {
    updateDeviceStatusList () {
      // 使用props传入的数据更新设备状态列表
      this.deviceStatusList = [
        {
          name: '运行中',
          type: 'running',
          count: this.machineReportData.normal || 0,
          icon: require('@/assets/images/dashboard/runing.png')
        },
        {
          name: '离线',
          type: 'offline',
          count: this.machineReportData.offLine || 0,
          icon: require('@/assets/images/dashboard/offline.png')
        },
        {
          name: '故障',
          type: 'error',
          count: this.machineReportData.error || 0,
          icon: require('@/assets/images/dashboard/error.png')
        },
        {
          name: '运营期',
          type: 'expire',
          count: this.machineReportData.expire || 0,
          icon: require('@/assets/images/dashboard/expire.png')
        }
      ]
    }
  }
}
</script>

<style lang="less" scoped>
.device-status-container {
    width: 100%;
    height: 100%;
    padding: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: var(--base-gap, 20px);
  width: 100%;
  height: 100%;
  max-width: 400px;
  max-height: 300px;
  min-width: 300px;
  min-height: 200px;
}

.status-item {
    display: flex;
    align-items: center;
    padding: var(--panel-padding, 12px);
    border-radius: var(--border-radius, 10px);
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: all 0.3s ease;
    cursor: pointer;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, transparent 0%, rgba(255, 255, 255, 0.05) 50%, transparent 100%);
        opacity: 0;
        transition: opacity 0.3s ease;
    }

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);

        &::before {
            opacity: 1;
        }
    }

    // 运行中状态 - 绿色主题
    &.status-running {
        background: linear-gradient(135deg, rgba(46, 204, 113, 0.1) 0%, rgba(39, 174, 96, 0.05) 100%);

        &:hover {
            box-shadow: 0 8px 25px rgba(46, 204, 113, 0.2);
        }

        .status-count {
            color: #2ecc71;
            text-shadow: 0 0 10px rgba(46, 204, 113, 0.5);
        }
    }

    // 离线状态 - 灰色主题
    &.status-offline {
        background: linear-gradient(135deg, rgba(149, 165, 166, 0.1) 0%, rgba(127, 140, 141, 0.05) 100%);

        &:hover {
            box-shadow: 0 8px 25px rgba(149, 165, 166, 0.2);
        }

        .status-count {
            color: #95a5a6;
            text-shadow: 0 0 10px rgba(149, 165, 166, 0.5);
        }
    }

    // 故障状态 - 红色主题
    &.status-error {
        background: linear-gradient(135deg, rgba(231, 76, 60, 0.1) 0%, rgba(192, 57, 43, 0.05) 100%);

        &:hover {
            box-shadow: 0 8px 25px rgba(231, 76, 60, 0.2);
        }

        .status-count {
            color: #e74c3c;
            text-shadow: 0 0 10px rgba(231, 76, 60, 0.5);
        }
    }

    // 过期状态 - 橙色主题
    &.status-expire {
        background: linear-gradient(135deg, rgba(230, 126, 34, 0.1) 0%, rgba(211, 84, 0, 0.05) 100%);

        &:hover {
            box-shadow: 0 8px 25px rgba(230, 126, 34, 0.2);
        }

        .status-count {
            color: #e67e22;
            text-shadow: 0 0 10px rgba(230, 126, 34, 0.5);
        }
    }
}

.status-icon {
    width: 55px;
    height: 55px;
    margin-right: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(5px);
    min-width: 55px;
    flex-shrink: 0;

    .icon-img {
        width: 36px;
        height: 36px;
        object-fit: contain;
        filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.3));
    }
}

.status-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.status-name {
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 4px;
  font-weight: 500;
  letter-spacing: 0.5px;
  transition: color 0.3s ease;
}

.status-count {
    font-weight: bold;
    color: #ffffff;
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
    font-family: 'Arial', 'Microsoft YaHei', sans-serif;
    transition: font-size 0.3s ease;
}

// 响应式设计 - 超大屏幕 (1920px+)
@media (min-width: 1920px) {
    .device-status-container {
        padding: 20px;
    }

    .status-grid {
        gap: 20px;
        max-width: 550px;
        max-height: 380px;
    }

    .status-item {
        padding: 16px;
    }

    .status-icon {
        width: 65px;
        height: 65px;
        margin-right: 15px;

        .icon-img {
            width: 42px;
            height: 42px;
        }
    }

    .status-name {
        margin-bottom: 6px;
    }
}

// 大屏幕 (1440px - 1919px)
@media (min-width: 1440px) and (max-width: 1919px) {
    .device-status-container {
        padding: 18px;
    }

    .status-grid {
        gap: 18px;
        max-width: 500px;
        max-height: 350px;
    }

    .status-item {
        padding: 14px;
    }

    .status-icon {
        width: 60px;
        height: 60px;
        margin-right: 14px;

        .icon-img {
            width: 38px;
            height: 38px;
        }
    }

    .status-name {
        margin-bottom: 5px;
    }
}

// 中等屏幕 (1200px - 1439px)
@media (min-width: 1200px) and (max-width: 1439px) {
    .status-grid {
        max-width: 480px;
        max-height: 340px;
    }

    .status-item {
        padding: 13px;
    }

    .status-icon {
        width: 58px;
        height: 58px;
        margin-right: 13px;

        .icon-img {
            width: 37px;
            height: 37px;
        }
    }

    .status-name {
        margin-bottom: 4.5px;
    }
}

// 小屏幕桌面 (1024px - 1199px)
@media (min-width: 1024px) and (max-width: 1199px) {
    .status-grid {
        max-width: 420px;
        max-height: 300px;
    }

    .status-item {
        padding: 11px;
    }

    .status-icon {
        width: 52px;
        height: 52px;
        margin-right: 11px;

        .icon-img {
            width: 34px;
            height: 34px;
        }
    }

    .status-name {
        margin-bottom: 3.5px;
    }
}

// 小屏幕平板 (768px - 1023px)
@media (min-width: 768px) and (max-width: 1023px) {
    .device-status-container {
        padding: 12px;
    }

    .status-grid {
        gap: 14px;
        max-width: 400px;
        max-height: 280px;
    }

    .status-item {
        padding: 10px;
    }

    .status-icon {
        width: 50px;
        height: 50px;
        margin-right: 10px;

        .icon-img {
            width: 32px;
            height: 32px;
        }
    }

    .status-name {
        margin-bottom: 3px;
    }
}

// 手机屏幕 (最大768px)
@media (max-width: 767px) {
    .device-status-container {
        padding: 10px;
    }

    .status-grid {
        grid-template-columns: 1fr;
        grid-template-rows: repeat(4, 1fr);
        gap: 10px;
        max-width: 100%;
        max-height: none;
    }

    .status-item {
        padding: 8px;
    }

    .status-icon {
        width: 45px;
        height: 45px;
        margin-right: 8px;

        .icon-img {
            width: 30px;
            height: 30px;
        }
    }

    .status-name {
        margin-bottom: 2px;
    }
}

// 动画效果
@keyframes pulse-glow {
    0%, 100% {
        box-shadow: 0 0 5px rgba(255, 255, 255, 0.1);
    }
    50% {
        box-shadow: 0 0 20px rgba(255, 255, 255, 0.2);
    }
}

.status-item {
    animation: pulse-glow 4s ease-in-out infinite;
}

// 为不同状态添加特殊动画延迟
.status-running {
    animation-delay: 0s;
}

.status-offline {
    animation-delay: 1s;
}

.status-error {
    animation-delay: 2s;
}

.status-expire {
    animation-delay: 3s;
}
</style>
